import { useEffect, useRef, useState } from 'react';
import { useChat } from '@/hooks/useChat';
import { ChatMessage } from '@/components/ChatMessage';
import { ChatInput } from '@/components/ChatInput';
import { ConversationList } from '@/components/ConversationList';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Chat() {
  const {
    conversations,
    currentConversation,
    currentConversationId,
    isLoading,
    createNewConversation,
    sendMessage,
    deleteConversation,
    setCurrentConversationId,
  } = useChat();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentConversation?.messages]);

  useEffect(() => {
    if (conversations.length === 0) {
      createNewConversation();
    }
  }, []);

  return (
    <div className="flex h-screen bg-slate-950 text-slate-100">
      {/* Sidebar */}
      <div className={`${showSidebar ? 'w-64' : 'w-0'} transition-all duration-300 overflow-hidden`}>
        <ConversationList
          conversations={conversations}
          currentId={currentConversationId}
          onNew={createNewConversation}
          onSelect={setCurrentConversationId}
          onDelete={deleteConversation}
        />
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-800 p-4 flex justify-between items-center bg-slate-900">
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            ☰
          </button>
          <h1 className="text-lg font-semibold">Chat with Grok</h1>
          <div className="w-8" />
        </div>

        {/* Messages Area */}
        <ScrollArea className="flex-1 p-6 overflow-y-auto">
          {!currentConversation || currentConversation.messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <h2 className="text-2xl font-semibold mb-2">Start a conversation</h2>
                <p className="text-slate-400">Ask me anything!</p>
              </motion.div>
            </div>
          ) : (
            <div>
              {currentConversation.messages.map((msg) => (
                <ChatMessage key={msg.id} message={msg} />
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-2 text-slate-400"
                >
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </motion.div>
              )}
              <div ref={scrollRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t border-slate-800 p-6 bg-slate-900">
          <ChatInput onSend={sendMessage} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
}
